import { z } from "zod";
import { Divider } from "@heroui/react";
import type { ReactNode } from "react";

import i18n from "@/i18n";
import {
  CheckboxField,
  InputField,
} from "@/features/FormCard/Fields";
import { parseFloatValue, parseIntValue } from "@/features/FormCard/utils";
import type { EditForm } from "@/features/Edit/types.ts";

const table_name = "k1_pe_funds";

const label = (key: string, defaultValue: string) =>
  i18n.t(`${table_name}.columns.${key}`, { ns: "tables", defaultValue });

function Section({
  title,
  isLast,
  children,
}: {
  title: string;
  isLast?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{children}</div>
      {!isLast && <Divider className="mt-2" />}
    </div>
  );
}

const intField = () =>
  z.preprocess(
    (val) => parseIntValue(val as string),
    z.number().int().nullable().optional(),
  );

const floatField = () =>
  z.preprocess(
    (val) => parseFloatValue(val as string),
    z.number().nullable().optional(),
  );

const DATE_FORMAT_MESSAGE = "Must be a valid calendar date (YYYY-MM-DD)";

const isValidCalendarDate = (value: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
  );
};

const dateField = () =>
  z.preprocess(
    (val) => (val === "" ? null : val),
    z.string().refine(isValidCalendarDate, DATE_FORMAT_MESSAGE).nullable().optional(),
  );

// Canonicalizes the recognized shapes (comma string, string array) into a
// plain string array, mirroring k1ManagementCompaniesEditForm's contacts field.
const toOldNamesShape = (value: unknown): unknown => {
  if (value == null || value === "") return [];

  if (typeof value === "string") {
    return value
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.map((name) => String(name).trim()).filter(Boolean);
  }

  return value;
};

const adjustOldNamesForCompare = (data: Record<string, any>) => ({
  ...data,
  old_names: toOldNamesShape(data.old_names),
});

const oldNamesField = () =>
  z.preprocess((value) => toOldNamesShape(value), z.array(z.string().min(1)));

const oldNamesToDisplayString = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "string") return value;
  return "";
};

export const k1PeFundsEditForm: EditForm = {
  table_name,
  adjustData: adjustOldNamesForCompare,
  formSchema: z.object({
    fund_name: z.string().min(1, "Fund name is required"),
    k1_gp_id_fonds: z.string().nullable().optional(),
    old_names: oldNamesField(),
    name_of_general_partner: z.string().nullable().optional(),
    manager: z.string().nullable().optional(),
    legal_form_fund: z.string().nullable().optional(),
    fund_investment_state: z.string().nullable().optional(),
    fund_domicile: z.string().nullable().optional(),
    team_location_country: z.string().nullable().optional(),
    fund_with_connection_to_germany: z.boolean().nullable().optional(),
    fund_currency: z.string().nullable().optional(),
    commitment_date: dateField(),
    signature_date: dateField(),
    liquidation_date: dateField(),
    vintage_year: intField().refine(
      (val) => val == null || (val >= 1900 && val <= 2100),
      "Must be between 1900 and 2100",
    ),
    first_closing_date: dateField(),
    final_closing_date: dateField(),
    first_investment_date_fund: dateField(),
    end_of_investment_period: dateField(),
    planned_end_date: dateField(),
    generation: z.string().nullable().optional(),
    sector_focus: z.string().nullable().optional(),
    strategy_focus: z.string().nullable().optional(),
    geographic_focus: z.string().nullable().optional(),
    asset_type_fund: z.string().nullable().optional(),
    product_type: z.string().nullable().optional(),
    lei_code_fund: z.string().nullable().optional(),
    in_which_closing_joined: z.string().nullable().optional(),
    pct_of_private_investors: floatField().refine(
      (val) => val == null || (val >= 0 && val <= 1),
      "Must be between 0 and 1 (stored as a fraction, e.g. 0.35 for 35%)",
    ),
    gp_name: z.string().nullable().optional(),
    management_company_id: intField().refine(
      (val) => val == null || val > 0,
      "Must be a positive ID",
    ),
    trr_required: z.boolean().nullable().optional(),
    deadline_nb_days: intField().refine(
      (val) => val == null || val >= 0,
      "Must be zero or more",
    ),
    first_production: z.boolean().nullable().optional(),
  }),
  formBodyContent: (form, multiFieldOptions) => (
    <div className="flex flex-col gap-6">
      <Section title="Fund Identity">
        <InputField
          name="fund_name"
          label={label("fund_name", "Fund Name")}
          isRequired
          control={form.control}
          fieldOptions={{ isReadOnly: true }}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="k1_gp_id_fonds"
          label={label("k1_gp_id_fonds", "GP ID Fonds")}
          control={form.control}
          fieldOptions={{ isReadOnly: true }}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="old_names"
          label={label("old_names", "Mapping Name")}
          control={form.control}
          valueMapper={oldNamesToDisplayString}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="name_of_general_partner"
          label={label("name_of_general_partner", "General Partner")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="manager"
          label={label("manager", "Manager")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="legal_form_fund"
          label={label("legal_form_fund", "Legal Form")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="fund_investment_state"
          label={label("fund_investment_state", "Investment Status")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
      </Section>

      <Section title="Location & Currency">
        <InputField
          name="fund_domicile"
          label={label("fund_domicile", "Fund Domicile")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="team_location_country"
          label={label("team_location_country", "Team Location")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="fund_currency"
          label={label("fund_currency", "Currency")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <CheckboxField
          name="fund_with_connection_to_germany"
          label={label(
            "fund_with_connection_to_germany",
            "Connection to Germany",
          )}
          control={form.control}
          className="self-center"
          multiFieldOptions={multiFieldOptions}
        />
      </Section>

      <Section title="Key Dates">
        <InputField
          name="commitment_date"
          label={label("commitment_date", "Commitment Date")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="signature_date"
          label={label("signature_date", "Signature Date")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="liquidation_date"
          label={label("liquidation_date", "Liquidation Date")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="vintage_year"
          label={label("vintage_year", "Vintage Year")}
          type="number"
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="first_closing_date"
          label={label("first_closing_date", "First Closing")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="final_closing_date"
          label={label("final_closing_date", "Final Closing")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="first_investment_date_fund"
          label={label("first_investment_date_fund", "First Investment")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="end_of_investment_period"
          label={label("end_of_investment_period", "End of Inv. Period")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="planned_end_date"
          label={label("planned_end_date", "Planned End")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
      </Section>

      <Section title="Strategy & Classification">
        <InputField
          name="generation"
          label={label("generation", "Generation")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="sector_focus"
          label={label("sector_focus", "Sector Focus")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="strategy_focus"
          label={label("strategy_focus", "Strategy Focus")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="geographic_focus"
          label={label("geographic_focus", "Geographic Focus")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="asset_type_fund"
          label={label("asset_type_fund", "Asset Type")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="product_type"
          label={label("product_type", "Product Type")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="lei_code_fund"
          label={label("lei_code_fund", "LEI Code")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
      </Section>

      <Section title="GP & Reporting" isLast>
        <InputField
          name="in_which_closing_joined"
          label={label("in_which_closing_joined", "Closing Joined")}
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="pct_of_private_investors"
          label={label(
            "pct_of_private_investors",
            "% Private Investors (fraction, e.g. 0.35 for 35%)",
          )}
          type="number"
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="gp_name"
          label={label("gp_name", "GP")}
          control={form.control}
          fieldOptions={{ isReadOnly: true }}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="management_company_id"
          label={label("management_company_id", "GP-ID")}
          type="number"
          control={form.control}
          fieldOptions={{ isReadOnly: true }}
          multiFieldOptions={multiFieldOptions}
        />
        <InputField
          name="deadline_nb_days"
          label={label("deadline_nb_days", "Reporting deadline")}
          type="number"
          control={form.control}
          multiFieldOptions={multiFieldOptions}
        />
        <CheckboxField
          name="trr_required"
          label={label("trr_required", "TRR required")}
          control={form.control}
          className="self-center"
          multiFieldOptions={multiFieldOptions}
        />
        <CheckboxField
          name="first_production"
          label={label("first_production", "Included in 1st prod")}
          control={form.control}
          className="self-center"
          multiFieldOptions={multiFieldOptions}
        />
      </Section>
    </div>
  ),
};
